import { useState } from 'react';

import { adminApi } from '../adminApi';

// Shared approve / reject / delete controls for a single device manual
// (ADR 0076). Used by BOTH manual surfaces — the cross-model review queue
// (AdminManuals) and the per-model device page (AdminDeviceDetail) — so the
// three byte-deleting actions can never drift apart again.
//
// Before this component each of the three destructive surfaces (two Rejects +
// the device-page Delete) rendered as the lightest `.admin-link-button` with no
// confirmation, even though Reject and Delete permanently remove the stored PDF.
// Here every destructive verb is:
//   - styled as `.admin-button-danger` (red outline), visually heavier than the
//     affirmative Approve, and
//   - gated by an inline two-step arm → fire confirm that names the
//     consequence, with both verbs disabled while a request is in flight.
//
// Approve is NOT gated (it is recoverable — a published manual can be deleted
// later), but its label states the consequence ("Approve & publish").

export type ManualAction = 'approve' | 'reject' | 'delete';

interface ManualReviewActionsProps {
  manualId: string;
  // Which verbs to render. The review queue and suggested rows use
  // ['approve', 'reject']; a published manual on the device page uses ['delete'].
  actions: ManualAction[];
  // Required for 'delete', which is scoped to the model:
  // DELETE /v1/admin/devices/:deviceModelId/manuals/:manualId.
  deviceModelId?: string;
  // Called after a successful action so the parent can drop / reload the row.
  onDone: (action: ManualAction) => void;
}

const CONFIRM_COPY: Record<'reject' | 'delete', string> = {
  // Scoped to the source URL: the row is retained keyed on source_url, so only
  // that exact URL is blocked from re-suggestion (a different URL or a re-upload
  // is not) — the copy must not over-promise.
  reject: 'Delete this PDF? This source URL won’t be suggested again.',
  delete: 'Delete this published manual? Owners of this model will lose access.',
};

export function ManualReviewActions({
  manualId,
  actions,
  deviceModelId,
  onDone,
}: ManualReviewActionsProps): React.ReactElement {
  // Which destructive verb is currently armed (awaiting a confirming click),
  // null when nothing is armed. Local to the row — independent rows arm
  // independently, which is fine; the safety property is the two-click gate.
  const [arming, setArming] = useState<'reject' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Arm / disarm a destructive verb, always clearing a stale error from a prior
  // failed attempt so the operator never sees a leftover message next to a
  // fresh confirm.
  function setArmed(verb: 'reject' | 'delete' | null): void {
    setError(null);
    setArming(verb);
  }

  async function run(action: ManualAction): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (action === 'delete') {
        if (deviceModelId === undefined) {
          throw new Error('missing device model id');
        }
        await adminApi(`/v1/admin/devices/${deviceModelId}/manuals/${manualId}`, {
          method: 'DELETE',
        });
      } else {
        await adminApi(`/v1/admin/manuals/${manualId}/${action}`, { method: 'POST' });
      }
      setArming(null);
      onDone(action);
    } catch (err) {
      const apiMessage = (err as { body?: { message?: string } }).body?.message;
      setError(apiMessage ?? (err instanceof Error ? err.message : `Failed to ${action} manual.`));
    } finally {
      setBusy(false);
    }
  }

  // While a destructive verb is armed, show only the inline confirm so the
  // armed action and the other verbs can't interleave.
  if (arming !== null) {
    return (
      <span className="admin-confirm">
        <span className="admin-confirm-prompt">{CONFIRM_COPY[arming]}</span>
        <button
          type="button"
          className="admin-button-danger"
          onClick={() => void run(arming)}
          disabled={busy}
        >
          {busy ? 'Working…' : arming === 'reject' ? 'Reject' : 'Delete'}
        </button>
        <button
          type="button"
          className="admin-button-secondary"
          onClick={() => setArmed(null)}
          disabled={busy}
        >
          Cancel
        </button>
        {error !== null ? <span className="admin-inline-error">{error}</span> : null}
      </span>
    );
  }

  return (
    <span className="admin-confirm">
      {actions.includes('approve') ? (
        <button
          type="button"
          className="admin-button-affirm"
          onClick={() => void run('approve')}
          disabled={busy}
          title="Publishes this PDF to every owner of the model"
        >
          Approve &amp; publish
        </button>
      ) : null}
      {actions.includes('reject') ? (
        <button
          type="button"
          className="admin-button-danger"
          onClick={() => setArmed('reject')}
          disabled={busy}
        >
          Reject
        </button>
      ) : null}
      {actions.includes('delete') ? (
        <button
          type="button"
          className="admin-button-danger"
          onClick={() => setArmed('delete')}
          disabled={busy}
        >
          Delete
        </button>
      ) : null}
      {error !== null ? <span className="admin-inline-error">{error}</span> : null}
    </span>
  );
}
